import { Modal, Input, Button, Descriptions, Alert, Space, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  GET_ORDER_STANDARDIZED_ADDRESS,
  GetOrderStandardizedAddressResponse,
  OrderStandardizedAddressType,
} from '../graphql/queries/getOrderStandardizedAddress';

const { Text } = Typography;

interface OrderStandardizedAddressModalProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * 订单标准化地址查询：输入运单号 → 展示 order_address 里的标准化地址 id / 标准化地址 / 经纬度。
 * 只读排查工具，底部仅「关闭」；关闭时清空输入与结果。
 */
const OrderStandardizedAddressModal: React.FC<OrderStandardizedAddressModalProps> = ({ visible, onClose }) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [result, setResult] = useState<OrderStandardizedAddressType | null | undefined>(undefined);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [queryAddress, { loading }] = useLazyQuery<GetOrderStandardizedAddressResponse>(GET_ORDER_STANDARDIZED_ADDRESS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setErrorText(null);
      setResult(data?.getOrderStandardizedAddress ?? null);
    },
    onError: (err) => {
      setResult(undefined);
      setErrorText(`查询失败：${err.message}`);
    },
  });

  useEffect(() => {
    if (!visible) {
      setTrackingNumber('');
      setResult(undefined);
      setErrorText(null);
    }
  }, [visible]);

  const handleSearch = () => {
    const value = trackingNumber.trim();
    if (!value) {
      setErrorText('请输入运单号');
      setResult(undefined);
      return;
    }
    queryAddress({ variables: { trackingNumber: value } });
  };

  const renderResult = () => {
    if (result === undefined) return null;
    if (result === null) {
      return <Alert type="warning" showIcon message="未找到订单" description="请确认运单号 / 渠道订单号 / 外部运单号是否正确" />;
    }
    const coordinates = result.latitude && result.longitude ? `${result.latitude}, ${result.longitude}` : '-';
    return (
      <>
        {result.orderAddressId === null && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="订单存在，但未找到收件地址" />
        )}
        {result.orderAddressId !== null && result.addressCoreId === null && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message="该收件地址尚未标准化（address_core_id 为空）" />
        )}
        <Descriptions column={1} bordered size="small" title="订单信息" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="订单 ID">{result.orderId}</Descriptions.Item>
          <Descriptions.Item label="运单号">{result.trackingNumber || '-'}</Descriptions.Item>
        </Descriptions>
        <Descriptions column={1} bordered size="small" title="订单原始地址" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="街道信息">{result.originalStreetAddress || '-'}</Descriptions.Item>
          <Descriptions.Item label="地址行 2">{result.originalBuilding || '-'}</Descriptions.Item>
          <Descriptions.Item label="城市">{result.originalCity || '-'}</Descriptions.Item>
          <Descriptions.Item label="州">{result.originalState || '-'}</Descriptions.Item>
          <Descriptions.Item label="邮编">{result.originalPostalCode || '-'}</Descriptions.Item>
        </Descriptions>
        <Descriptions column={1} bordered size="small" title="标准化结果">
          <Descriptions.Item label="标准化地址 ID">
            {result.addressCoreId !== null ? <Text copyable>{String(result.addressCoreId)}</Text> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="标准化地址">
            {result.standardizedAddress ? <Text copyable>{result.standardizedAddress}</Text> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="经纬度">
            {coordinates !== '-' ? <Text copyable>{coordinates}</Text> : '-'}
          </Descriptions.Item>
        </Descriptions>
      </>
    );
  };

  return (
    <Modal
      title="订单标准化地址查询"
      open={visible}
      onCancel={onClose}
      footer={
        <Button onClick={onClose} disabled={loading}>
          关闭
        </Button>
      }
      width={640}
      maskClosable={!loading}
      destroyOnClose
    >
      <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
        <Input
          placeholder="请输入运单号 / 渠道订单号 / 外部运单号"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
          disabled={loading}
        />
        <Button type="primary" onClick={handleSearch} loading={loading}>
          查询
        </Button>
      </Space.Compact>

      {errorText && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorText} />}
      {renderResult()}
    </Modal>
  );
};

export default OrderStandardizedAddressModal;
